import { useEffect, useMemo, useRef, useState } from 'react';
import type { LivingScene as LivingSceneDef } from '@/contracts/content';
import { Scene } from '@/content/scenes';
import { Button } from '@/design-system/Button';
import { SparkleField } from '@/design-system/Sparkle';
import { IconPlay } from '@/features/studio/Icons';
import { artworkTransformAt } from './timeline';
import { recordReveal, supportedVideoFormat } from '@/services/reveal';
import { Platform } from '@/platform';
import { IconFilm } from '@/features/studio/Icons';

interface Props {
  scene: LivingSceneDef;
  /** The child's own finished picture, as a data URL. */
  artworkUrl: string;
  /** Background scene key from the story. */
  backdropSceneId: string;
  /** Used to name the saved video. */
  title?: string;
  onClose?: () => void;
}

/**
 * Bring my story to life (spec §10.2).
 *
 * The animation is authored, deterministic, replayable, and runs entirely
 * offline — there is no generation wait and nothing reinterprets the drawing.
 * What moves is the child's ACTUAL picture, with their brush marks and their
 * colours. Reduced motion collapses it to the authored static arrangement
 * with an accessible description instead of silently animating anyway.
 */
export function LivingScene({ scene, artworkUrl, backdropSceneId, title, onClose }: Props) {
  const prefersReduced = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );
  const [playing, setPlaying] = useState(!prefersReduced);
  const [t, setT] = useState(prefersReduced ? scene.durationMs : 0);
  const raf = useRef(0);
  const start = useRef(0);

  useEffect(() => {
    if (!playing) return;
    start.current = performance.now() - t;
    const tick = (now: number) => {
      const elapsed = now - start.current;
      if (elapsed >= scene.durationMs) {
        setT(scene.durationMs);
        setPlaying(false);
        return;
      }
      setT(elapsed);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // Resolved by the shared timeline, which the video export also plays — so
  // the file a family keeps is the animation they actually watched.
  const transform = useMemo(() => artworkTransformAt(scene, t), [t, scene]);

  const replay = () => {
    setT(0);
    setPlaying(true);
  };

  /* ---------------- Keeping it as a video (spec §0.1 F20) ---------------- */

  // Probed once, for real. Recording is missing or partial on several
  // browsers, so the control is simply absent where it cannot work rather
  // than present and disappointing (§7 C06-C07).
  const canRecord = useMemo(() => supportedVideoFormat() !== null, []);
  const [video, setVideo] = useState<VideoState>({ kind: 'idle' });
  const abort = useRef<AbortController | null>(null);

  const saveVideo = async () => {
    if (video.kind === 'working') return;
    const controller = new AbortController();
    abort.current = controller;
    setVideo({ kind: 'working', done: 0, step: 'Getting the scene ready' });
    // Play it on screen at the same time — the child watches the thing that is
    // being recorded, rather than a progress bar over a frozen picture.
    setT(0);
    setPlaying(true);
    try {
      const { blob, ext } = await recordReveal({
        scene,
        artworkUrl,
        backdropSceneId,
        signal: controller.signal,
        onProgress: (p) => setVideo({ kind: 'working', done: p.done, step: p.step }),
      });
      const filename = `${(title ?? 'my-picture').replace(/\W+/g, '-').toLowerCase()}-coming-to-life.${ext}`;
      const result = await Platform.share.export({ blob, filename, title: filename });
      if (result.status === 'unsupported') {
        setVideo({
          kind: 'failed',
          message:
            'The video was made, but this shared preview runs in a sandbox that will not let a page save files to your device. It saves normally in the real app.',
        });
        return;
      }
      setVideo({ kind: result.status === 'cancelled' ? 'idle' : 'saved' });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        setVideo({ kind: 'idle' });
        return;
      }
      setVideo({
        kind: 'failed',
        message: 'That video could not be made. Your picture and the scene are both fine.',
      });
    } finally {
      abort.current = null;
    }
  };

  return (
    <div className="relative overflow-hidden rounded-blob border-2 border-line bg-ink shadow-cut-lg">
      <div className="relative aspect-[3/2] w-full">
        <Scene id={backdropSceneId} className="absolute inset-0 h-full w-full" />
        <SparkleField count={10} seed={3} className="opacity-60" />
        <img
          src={artworkUrl}
          alt="Your picture, in the story"
          className="absolute left-1/2 top-1/2 w-[58%] max-w-[380px] [filter:drop-shadow(0_0_18px_rgba(255,232,160,0.45))_drop-shadow(0_14px_26px_rgba(0,0,0,0.5))]"
          style={{
            transform: `translate(-50%, -50%) translate(${transform.x}%, ${transform.y}%) scale(${transform.scale}) rotate(${transform.rotate}deg)`,
            opacity: transform.opacity,
            willChange: 'transform, opacity',
          }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-m bg-card p-l">
        {prefersReduced ? (
          <p className="flex-1 text-[15px] text-ink-soft">{scene.staticDescription}</p>
        ) : (
          <p className="flex-1 text-[15px] text-ink-soft">
            Your picture, moving in the story — with the colours you chose.
          </p>
        )}
        <Button variant="soft" size="md" onClick={replay} icon={<IconPlay />}>
          {playing ? 'Playing…' : 'Play again'}
        </Button>
        {canRecord ? (
          <Button
            variant="primary"
            size="md"
            onClick={saveVideo}
            loading={video.kind === 'working'}
            icon={<IconFilm />}
          >
            {video.kind === 'saved' ? 'Saved' : 'Keep the video'}
          </Button>
        ) : null}
        {onClose ? (
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        ) : null}
      </div>

      {video.kind === 'working' ? (
        <div className="border-t-2 border-line bg-card px-l pb-l">
          <p className="text-[15px] text-ink-soft" role="status">
            {video.step} — it records while it plays, so this takes about{' '}
            {Math.round(scene.durationMs / 1000)} seconds.
          </p>
          <div className="mt-s h-2 w-full overflow-hidden rounded-full bg-paper-deep">
            <div
              className="h-full rounded-full bg-magic transition-[width]"
              style={{ width: `${Math.round(video.done * 100)}%` }}
            />
          </div>
          <button
            onClick={() => abort.current?.abort()}
            className="mt-m min-h-touch rounded-pebble border-2 border-line px-m font-display text-[14px] font-semibold text-ink-soft"
          >
            Stop
          </button>
        </div>
      ) : null}

      {video.kind === 'failed' ? (
        <p role="alert" className="border-t-2 border-line bg-secondary-soft px-l py-m text-[15px] text-ink">
          {video.message}
        </p>
      ) : null}
    </div>
  );
}

type VideoState =
  | { kind: 'idle' }
  | { kind: 'working'; done: number; step: string }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string };
